import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MapPin, Building2 } from 'lucide-react';
import LoadingSpinner from '../common/LoadingSpinner';
import FormCard from '../common/FormCard';
import ErrorAlert from '../common/ErrorAlert';

const OAuthGymSelection = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [gyms, setGyms] = useState([]);
  const [selectedGymId, setSelectedGymId] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [userData, setUserData] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

  useEffect(() => {
    // Parse user data from URL
    const dataParam = searchParams.get('data');
    if (!dataParam) {
      navigate('/login');
      return;
    }

    try {
      const parsed = JSON.parse(decodeURIComponent(dataParam));
      setUserData(parsed);
    } catch (err) {
      console.error('Failed to parse user data:', err);
      navigate('/login');
      return;
    }

    // Fetch available gyms
    fetchGyms();
  }, [searchParams, navigate]);

  const fetchGyms = async () => {
    try {
      // Use the public endpoint that doesn't require authentication
      const response = await fetch(`${API_URL}/api/gyms/public`);
      if (!response.ok) throw new Error('Failed to fetch gyms');

      const data = await response.json();
      setGyms(data);
    } catch (err) {
      setError('Failed to load gyms. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedGymId) {
      setError('Please select a gym');
      return;
    }

    setSubmitting(true);
    setError('');

    try {
      // Complete OAuth registration
      const response = await fetch(`${API_URL}/api/auth/oauth/complete-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          oauth_provider: userData.oauth_provider,
          oauth_id: userData.oauth_id,
          email: userData.email,
          firstName: userData.firstName,
          lastName: userData.lastName,
          gymId: parseInt(selectedGymId),
          profilePicture: userData.profilePicture
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Registration failed');
      }

      const data = await response.json();

      // Store token and update auth context
      localStorage.setItem('token', data.token);

      // Redirect to dashboard
      navigate('/dashboard');
      window.location.reload(); // Reload to update auth context
    } catch (err) {
      setError(err.message || 'Failed to complete registration');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter gyms based on search query
  const filteredGyms = gyms.filter(gym => {
    const searchLower = searchQuery.toLowerCase();
    return (
      gym.name?.toLowerCase().includes(searchLower) ||
      gym.city?.toLowerCase().includes(searchLower) ||
      gym.state?.toLowerCase().includes(searchLower) ||
      gym.address_line_1?.toLowerCase().includes(searchLower)
    );
  });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50">
        <LoadingSpinner size="large" text="Loading gyms..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl w-full">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-teal-600 via-purple-600 to-magenta-600 bg-clip-text text-transparent mb-3">
            Welcome{userData?.firstName ? `, ${userData.firstName}` : ''}!
          </h1>
          <p className="text-lg font-semibold text-slate-700">
            One more step to start climbing
          </p>
          <p className="text-base font-medium text-slate-600 mt-2">
            Select your primary gym to complete your registration
          </p>
        </div>

        {/* Main Form Card */}
        <FormCard
          title="Select Your Gym"
          showAccentBar={true}
          onSubmit={handleSubmit}
        >
          {/* Search Input */}
          <div className="mb-4">
            <label htmlFor="search" className="block text-sm font-bold text-slate-700 mb-2">
              Search Gyms
            </label>
            <input
              id="search"
              type="text"
              placeholder="Search by name, city, or state..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="appearance-none rounded-lg relative block w-full px-4 py-3 border-2 border-slate-900 placeholder-slate-500 text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all sm:text-base touch-manipulation"
            />
          </div>

          {/* Gym Selection */}
          <div className="mb-6">
            <label htmlFor="gym" className="block text-sm font-bold text-slate-700 mb-3">
              Available Gyms ({filteredGyms.length})
            </label>

            {filteredGyms.length === 0 ? (
              <div className="text-center py-8 bg-slate-50 rounded-lg border-2 border-slate-300">
                <Building2 className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                <p className="text-slate-600 font-medium">No gyms found</p>
                <p className="text-sm text-slate-500 mt-1">Try a different search term</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto border-2 border-slate-900 rounded-lg">
                {filteredGyms.map((gym) => (
                  <button
                    key={gym.id}
                    type="button"
                    onClick={() => setSelectedGymId(gym.id.toString())}
                    className={`w-full text-left p-4 transition-all touch-manipulation ${
                      selectedGymId === gym.id.toString()
                        ? 'bg-teal-50 border-2 border-teal-500'
                        : 'bg-white border-2 border-transparent hover:border-teal-300 hover:bg-teal-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        selectedGymId === gym.id.toString()
                          ? 'bg-teal-500'
                          : 'bg-slate-200'
                      }`}>
                        <Building2 className={`w-5 h-5 ${
                          selectedGymId === gym.id.toString()
                            ? 'text-white'
                            : 'text-slate-600'
                        }`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className={`font-bold text-base ${
                          selectedGymId === gym.id.toString()
                            ? 'text-teal-900'
                            : 'text-slate-900'
                        }`}>
                          {gym.name}
                        </h3>
                        {(gym.city || gym.state) && (
                          <div className="flex items-center gap-1 mt-1">
                            <MapPin className={`w-4 h-4 flex-shrink-0 ${
                              selectedGymId === gym.id.toString()
                                ? 'text-teal-600'
                                : 'text-slate-500'
                            }`} />
                            <p className={`text-sm font-medium ${
                              selectedGymId === gym.id.toString()
                                ? 'text-teal-700'
                                : 'text-slate-600'
                            }`}>
                              {[gym.city, gym.state].filter(Boolean).join(', ')}
                            </p>
                          </div>
                        )}
                      </div>
                      {selectedGymId === gym.id.toString() && (
                        <div className="flex-shrink-0">
                          <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center border-2 border-slate-900">
                            <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Error Alert */}
          {error && (
            <ErrorAlert
              message={error}
              className="mb-6"
              onDismiss={() => setError('')}
            />
          )}

          {/* Submit Button */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="flex-1 px-6 py-3 bg-white hover:bg-slate-50 text-slate-700 font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation"
            >
              Back to Login
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedGymId}
              className="flex-1 px-6 py-3 bg-teal-500 hover:bg-teal-600 text-white font-bold rounded-lg border-2 border-slate-900 shadow-retro hover:shadow-retro-hover transition-all touch-manipulation disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-retro"
            >
              {submitting ? (
                <span className="flex items-center justify-center gap-2">
                  <LoadingSpinner size="small" />
                  <span>Completing...</span>
                </span>
              ) : (
                'Complete Registration'
              )}
            </button>
          </div>
        </FormCard>

        {/* Help Text */}
        <p className="text-center text-sm font-medium text-slate-600 mt-6">
          Don't see your gym?{' '}
          <span className="text-teal-600 font-bold">Contact us</span> to get it added!
        </p>
      </div>
    </div>
  );
};

export default OAuthGymSelection;
